/**
 * The adjustable-permission switch panel (`chorus-byggeplan.md` fase 2, Settings; `ROLES.md` §7).
 *
 * ---------------------------------------------------------------------------------------------
 * NAMED `...MatrixRow`, NOT `...Row`. `policy/types.ts` already exports `permissionRowSchema`
 * for a DATABASE row — one (role, resource, verb, allowed) tuple. These are rows of the grid an
 * owner reads: one action, five cells. Two different things, and the older name was there first.
 *
 * THIS ENDPOINT ADDS NO SECURITY LOGIC. THAT IS THE POINT.
 *
 * The build plan is explicit: *"Selve bryterpanelet bygges i fase 2; ingen ny sikkerhetslogikk
 * trengs da, bare UI som endrer rollens rettighetssett."* Permissions have been data since Phase 0
 * — `role_permissions`, one row per (role, resource, verb) per organisation, read by
 * `ActorService` on every request. Nothing here teaches the system a new way to decide who may do
 * what. It only lets an owner change a value the system was already reading.
 *
 * So the interesting content of this file is what it REFUSES to expose.
 * ---------------------------------------------------------------------------------------------
 *
 * THREE LAYERS, AND ONLY ONE OF THEM IS WRITABLE (build plan rev. 1.1, §11.3.1).
 *
 * * **64 fixed actions.** The row exists and decides access, and no organisation may change it.
 *   Returned here as context — an owner deciding whether to give `member` the contact register
 *   needs to see what `member` already has — but every one carries `adjustable: false`.
 * * **10 adjustable actions.** The allow-list in `ADJUSTABLE_ACTIONS`. Decision 0006 made this an
 *   allow-list rather than a deny-list precisely so that a new action added in Phase 3 is NOT
 *   customer-adjustable by default; somebody has to put it on the list on purpose.
 * * **3 platform-locked actions.** `organization:delete`, `membership:manage_roles` and
 *   `role_permission:update`. The database refuses to store them as rows at all, so they cannot
 *   be granted by writing data — which is why they cannot appear in a request here either.
 *
 * WHO MAY SEE AND CHANGE THIS. `role_permission:list` and `:read` are `yes` for superadmin and
 * `no` for everyone else. `role_permission:update` is `yes` for superadmin and **locked** for the
 * other four: an organisation cannot configure its way into letting an admin edit the matrix,
 * because the lock is in code and the database will not hold the row.
 *
 * WHY A CELL CARRIES ITS SCOPE AND NOT JUST A BOOLEAN. A matrix cell is not `allowed: true/false`
 * — it is `yes`, `own`, `dept`, `part`, `grant`, `no` or `locked`. `own` and `dept` still GRANT
 * the action, narrowed, and the narrowing lives in `rules.ts` and in the query the API runs. A
 * switch panel that showed a checkbox for `dept` would be telling the owner that `limited` sees
 * every contact. So the scope travels with the cell, and the UI renders it.
 */
import { z } from 'zod';

import { ORG_ROLES } from '../enums.js';
import { isKnownAction } from '../policy/rules.js';

/**
 * What a cell is worth, on the wire.
 *
 * The same seven values `default-permissions.ts` uses, because an owner reading the panel and a
 * developer reading the matrix must be looking at the same thing. `locked` never travels: a
 * locked action is absent from the response entirely, since showing an owner a switch they can
 * never touch is worse than not showing the row.
 */
export const PERMISSION_SCOPES = ['yes', 'own', 'dept', 'part', 'grant', 'no'] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];
export const permissionScopeSchema = z.enum(PERMISSION_SCOPES).meta({ id: 'PermissionScope' });

export const permissionMatrixCellSchema = z
  .object({
    role: z.enum(ORG_ROLES),
    /**
     * The scope as it stands for this organisation.
     *
     * A SWITCH HAS TWO POSITIONS; THE CONSEQUENCE OF FLIPPING IT DOES NOT.
     *
     * This used to say an adjustable action is always `yes` or `no`. That is wrong for exactly
     * one cell today, and the API agent caught it rather than coding around it: `task:delete` is
     * adjustable AND `own` for `member` — `rules.ts` narrows it with
     * `byRole({ member: isCreatorOrAssignee })` whenever the row grants it. So granting it
     * reports `own`, not `yes`, because reporting `yes` would promise the owner something the
     * API does not do.
     *
     * The rule for the UI is therefore about the ROW and not about the value: an adjustable row
     * gets a switch, and its scope is still rendered, because `own` beside a switch that is on is
     * the honest picture — the role has it, narrowed.
     */
    scope: permissionScopeSchema,
  })
  .meta({ id: 'PermissionMatrixCell' });

export const permissionMatrixRowSchema = z
  .object({
    /** e.g. `contact:create`. The `resource:verb` pair, as one string. */
    action: z.string().refine(isKnownAction, { message: 'Unknown action.' }),
    /** e.g. `contact`. Carried so the UI can group without re-splitting the action. */
    resource: z.string(),
    /**
     * Whether this organisation may change it. False for all 64 fixed actions.
     *
     * The client must not derive this from anything else — `ADJUSTABLE_ACTIONS` lives in shared
     * and the client could read it directly, but then the list would have two homes and the API
     * would not be the contract (principle 7). A mobile app asks the same endpoint and gets the
     * same answer.
     */
    adjustable: z.boolean(),
    cells: z.array(permissionMatrixCellSchema),
  })
  .meta({ id: 'PermissionMatrixRow' });

export const rolePermissionMatrixSchema = z
  .object({
    /**
     * Every action this organisation's matrix decides, fixed and adjustable alike, in a stable
     * order. Locked actions are absent.
     */
    rows: z.array(permissionMatrixRowSchema),
    /**
     * The effective adjustable list for this organisation (0476): the founder's choice, else the
     * code's `ADJUSTABLE_ACTIONS`. The same as `rows[].adjustable`, as one list — plus nothing else.
     * The Rettigheter page reads this, never `ADJUSTABLE_ACTIONS`.
     */
    adjustable: z.array(z.string()),
  })
  .meta({ id: 'RolePermissionMatrix' });

/**
 * One switch, flipped.
 *
 * A cell at a time rather than a whole matrix: a `PUT` of the entire grid would let a client
 * overwrite a change somebody else made a second earlier, and there is no version to compare. One
 * cell is also what an owner actually does — nobody rewrites 385 values at once.
 *
 * `allowed` rather than a scope, because an adjustable action only ever has two positions. An
 * organisation cannot invent a `dept` narrowing for `contact:create`; the narrowings are rules in
 * code, not data.
 */
export const updateRolePermissionRequestSchema = z
  .object({
    role: z.enum(ORG_ROLES),
    action: z.string().refine(isKnownAction, { message: 'Unknown action.' }),
    allowed: z.boolean(),
  })
  .meta({ id: 'UpdateRolePermissionRequest' });

/**
 * One choice on the Rettigheter page, which may write several cells (docs/qa/rettigheter/UTKAST.md:
 * «Legge til og rette kontakter» is `contact:create` + `contact:update`, and a threshold moves every
 * role). All or nothing: the API checks every change before it writes one, in one transaction.
 *
 * The same (role, action) twice is refused — two values for one cell in one request has no single
 * meaning, and last-wins would hide a client bug.
 */
export const batchUpdateRolePermissionsRequestSchema = z
  .object({
    changes: z
      .array(updateRolePermissionRequestSchema)
      .min(1)
      .max(50)
      .refine(
        (changes) =>
          new Set(changes.map((change) => change.role + '|' + change.action)).size ===
          changes.length,
        { message: 'The same cell appears more than once.' },
      ),
  })
  .meta({ id: 'BatchUpdateRolePermissionsRequest' });

export type BatchUpdateRolePermissionsRequest = z.infer<
  typeof batchUpdateRolePermissionsRequestSchema
>;
export type PermissionMatrixCell = z.infer<typeof permissionMatrixCellSchema>;
export type PermissionMatrixRow = z.infer<typeof permissionMatrixRowSchema>;
export type RolePermissionMatrix = z.infer<typeof rolePermissionMatrixSchema>;
export type UpdateRolePermissionRequest = z.infer<typeof updateRolePermissionRequestSchema>;
